import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Badge } from '../../../components/ui/badge';
import { ArrowLeft, Star, StarOff, Send, Building2, Globe, Users, Calendar, TrendingUp, TrendingDown } from 'lucide-react';
import MarkdownMessage from '../../../components/ui/markdown-message';
import { getCompanyDetails } from '../../../data/stockScreenerService';
import { useWatchlistStore } from '../../../state/watchlistStore';
import { useAuthStore } from '../../../state/authStore';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SUGGESTED_QUESTIONS = [
  'What are the key financial strengths?',
  'What risks should I be aware of?',
  'How does it compare to competitors?',
  'Is this a good long-term investment?',
  'What is the growth outlook?',
];

export default function CompanyResearchPage() {
  const { symbol } = useParams();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const { isInWatchlist, addToWatchlist, removeFromWatchlist, getWatchlistItem } = useWatchlistStore();

  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [question, setQuestion] = useState('');
  const [chatHistory, setChatHistory] = useState([]);
  const [aiLoading, setAiLoading] = useState(false);

  useEffect(() => {
    loadCompany();
  }, [symbol]);

  const loadCompany = async () => {
    setLoading(true);
    let data = await getCompanyDetails(symbol);
    if (!data) {
      try {
        const cleanSymbol = symbol.replace(/\.NS$/, '').replace(/\.BO$/, '');
        const resp = await axios.get(`${API}/india/securities`, { params: { search: cleanSymbol, page_size: 1 } });
        const items = resp.data?.items;
        if (items && items.length > 0) {
          const s = items[0];
          data = {
            id: s.isin,
            name: s.company_name,
            symbol: symbol,
            exchange: s.nse_symbol ? 'NSE' : 'BSE',
            sector: s.sector || s.macro_sector || 'N/A',
            industry: s.industry || s.basic_industry || 'N/A',
            description: `${s.company_name} is listed on ${s.nse_symbol ? 'NSE' : 'BSE'}. ISIN: ${s.isin}. Sector: ${s.macro_sector || 'N/A'} / ${s.sector || 'N/A'}. Industry: ${s.industry || 'N/A'} / ${s.basic_industry || 'N/A'}.`,
            isin: s.isin,
            nse_symbol: s.nse_symbol,
            bse_code: s.bse_code,
          };
        }
      } catch (err) {
        console.error('India API fallback failed:', err);
      }
    }
    setCompany(data);
    setLoading(false);
  };

  const inWatchlist = company ? isInWatchlist(company.symbol) : false;

  const toggleWatchlist = () => {
    if (!company) return;
    if (inWatchlist) {
      const item = getWatchlistItem(company.symbol);
      if (item) removeFromWatchlist(item.id);
    } else {
      addToWatchlist({
        userId: user?.id || 'guest',
        companyId: company.id,
        symbol: company.symbol,
        companyName: company.name,
        exchange: company.exchange || 'NSE',
      });
    }
  };

  const askQuestion = async (q) => {
    const questionText = q || question;
    if (!questionText.trim() || !company) return;

    const userMsg = { role: 'user', content: questionText };
    setChatHistory((prev) => [...prev, userMsg]);
    setQuestion('');
    setAiLoading(true);

    try {
      const response = await axios.post(`${API}/chat`, {
        message: `About ${company.name} (${company.symbol}): ${questionText}`,
        context: `Company: ${company.name}, Sector: ${company.sector}, P/E: ${company.pe}, Market Cap: ${company.marketCap}, Current Price: ${company.currentPrice}`,
      });
      const aiMsg = { role: 'assistant', content: response.data.response || response.data.message || 'No response received.' };
      setChatHistory((prev) => [...prev, aiMsg]);
    } catch {
      const aiMsg = { role: 'assistant', content: `${company.name} is a ${company.sector} company listed on ${company.exchange}. ${company.description || ''}. Current P/E: ${company.pe || 'N/A'}, Market Cap: ₹${company.marketCap?.toLocaleString()} Cr.` };
      setChatHistory((prev) => [...prev, aiMsg]);
    } finally {
      setAiLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  if (!company) {
    return (
      <div className="max-w-4xl mx-auto text-center py-16">
        <p className="text-lg text-gray-500">Company not found for symbol: {symbol}</p>
        <Button onClick={() => navigate('/public/screener')} className="mt-4">Back to Screener</Button>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">{company.name}</h1>
            <p className="text-sm text-gray-500">{company.symbol} · {company.exchange}</p>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={toggleWatchlist} className="gap-2">
          {inWatchlist ? <StarOff className="w-4 h-4 text-yellow-500" /> : <Star className="w-4 h-4" />}
          {inWatchlist ? 'Remove from Watchlist' : 'Add to Watchlist'}
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Company Info & Fundamentals */}
        <div className="lg:col-span-1 space-y-4">
          {/* Price Card */}
          <Card>
            <CardContent className="p-4">
              <div className="text-3xl font-bold text-gray-900 dark:text-gray-100">
                ₹{company.currentPrice?.toLocaleString('en-IN') || 'N/A'}
              </div>
              {company.priceChangePercent != null && (
                <div className={`flex items-center gap-1 text-sm font-medium ${company.priceChangePercent >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                  {company.priceChangePercent >= 0 ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
                  {company.priceChange >= 0 ? '+' : ''}{company.priceChange?.toFixed(2)} ({company.priceChangePercent >= 0 ? '+' : ''}{company.priceChangePercent?.toFixed(2)}%)
                </div>
              )}
            </CardContent>
          </Card>

          {/* Company Details */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">Company Info</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              {company.description && <p className="text-gray-600 dark:text-gray-400">{company.description}</p>}
              <div className="grid grid-cols-2 gap-2 pt-2">
                {company.sector && (
                  <div className="flex items-center gap-2"><Building2 className="w-3 h-3 text-gray-400" /><span>{company.sector}</span></div>
                )}
                {company.website && (
                  <div className="flex items-center gap-2"><Globe className="w-3 h-3 text-gray-400" /><a href={company.website} target="_blank" rel="noreferrer" className="text-emerald-600 hover:underline truncate">{company.website.replace('https://www.', '')}</a></div>
                )}
                {company.ceo && (
                  <div className="flex items-center gap-2"><Users className="w-3 h-3 text-gray-400" /><span>CEO: {company.ceo}</span></div>
                )}
                {company.foundedYear && (
                  <div className="flex items-center gap-2"><Calendar className="w-3 h-3 text-gray-400" /><span>Founded: {company.foundedYear}</span></div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Fundamentals */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">Fundamentals</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-3 text-sm">
                {[
                  ['P/E', company.pe?.toFixed(1)],
                  ['P/B', company.pb?.toFixed(1)],
                  ['EPS', company.eps ? `₹${company.eps.toFixed(1)}` : null],
                  ['Dividend', company.dividend ? `${company.dividend}%` : null],
                  ['ROE', company.roe ? `${company.roe}%` : null],
                  ['D/E', company.debtToEquity?.toFixed(2)],
                  ['Book Value', company.bookValue ? `₹${company.bookValue.toFixed(1)}` : null],
                  ['Employees', company.employees?.toLocaleString()],
                ].map(([label, value]) => (
                  <div key={label} className="flex justify-between border-b border-gray-100 dark:border-gray-800 pb-1">
                    <span className="text-gray-500">{label}</span>
                    <span className="font-medium text-gray-900 dark:text-gray-100">{value || 'N/A'}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* AI Q&A */}
        <div className="lg:col-span-2">
          <Card className="h-full flex flex-col">
            <CardHeader className="pb-2">
              <CardTitle className="text-base">AI Research Assistant</CardTitle>
              <CardDescription>Ask any question about {company.name}</CardDescription>
            </CardHeader>
            <CardContent className="flex-1 flex flex-col">
              {/* Suggested Questions */}
              {chatHistory.length === 0 && (
                <div className="flex flex-wrap gap-2 mb-4">
                  {SUGGESTED_QUESTIONS.map((q) => (
                    <button
                      key={q}
                      onClick={() => askQuestion(q)}
                      className="px-3 py-1.5 text-xs rounded-full border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:bg-emerald-50 dark:hover:bg-emerald-950 hover:border-emerald-300 transition-colors"
                    >
                      {q}
                    </button>
                  ))}
                </div>
              )}

              {/* Chat History */}
              <div className="flex-1 overflow-y-auto space-y-3 mb-4 max-h-[500px]">
                {chatHistory.map((msg, i) => (
                  <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[80%] px-4 py-2.5 rounded-xl ${
                      msg.role === 'user'
                        ? 'bg-emerald-600 text-white rounded-br-sm text-sm'
                        : 'bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-200 rounded-bl-sm'
                    }`}>
                      {msg.role === 'user' ? msg.content : <MarkdownMessage content={msg.content} />}
                    </div>
                  </div>
                ))}
                {aiLoading && (
                  <div className="flex justify-start">
                    <div className="bg-gray-100 dark:bg-gray-800 px-4 py-3 rounded-xl rounded-bl-sm">
                      <div className="flex gap-1">
                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Input */}
              <div className="flex gap-2">
                <Input
                  placeholder="Ask about this company..."
                  value={question}
                  onChange={(e) => setQuestion(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && askQuestion()}
                  className="flex-1"
                />
                <Button onClick={() => askQuestion()} disabled={!question.trim() || aiLoading} className="bg-emerald-600 hover:bg-emerald-700">
                  <Send className="w-4 h-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
